import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Grid,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

interface Settings {
  theme: 'light' | 'dark';
  language: 'it' | 'en';
  notifications: boolean;
  syncFrequency: number;
  autoSync: boolean;
  debugMode: boolean;
}

const SettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<Settings>({
    theme: 'light',
    language: 'it',
    notifications: true,
    syncFrequency: 300000, // 5 minuti
    autoSync: true,
    debugMode: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Carica impostazioni dal localStorage per ora
      const savedSettings = localStorage.getItem('appSettings');
      if (savedSettings) {
        setSettings(JSON.parse(savedSettings));
      }
    } catch (error) {
      console.error('Errore nel caricamento delle impostazioni:', error);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Salva nel localStorage per ora
      localStorage.setItem('appSettings', JSON.stringify(settings));
      
      setMessage({ type: 'success', text: 'Impostazioni salvate con successo!' });
      
      // Nascondi il messaggio dopo 3 secondi
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Errore nel salvataggio delle impostazioni:', error);
      setMessage({ type: 'error', text: 'Errore nel salvataggio delle impostazioni' });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSettings({
      theme: 'light',
      language: 'it',
      notifications: true,
      syncFrequency: 300000,
      autoSync: true,
      debugMode: false
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Impostazioni
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Impostazioni Generali */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Impostazioni Generali
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Tema</InputLabel>
                  <Select
                    value={settings.theme}
                    onChange={(e) => setSettings(prev => ({ ...prev, theme: e.target.value as 'light' | 'dark' }))}
                  >
                    <MenuItem value="light">Chiaro</MenuItem>
                    <MenuItem value="dark">Scuro</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Lingua</InputLabel>
                  <Select
                    value={settings.language}
                    onChange={(e) => setSettings(prev => ({ ...prev, language: e.target.value as 'it' | 'en' }))}
                  >
                    <MenuItem value="it">Italiano</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications}
                      onChange={(e) => setSettings(prev => ({ ...prev, notifications: e.target.checked }))}
                    />
                  }
                  label="Abilita Notifiche"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Impostazioni Sincronizzazione */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sincronizzazione
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoSync}
                      onChange={(e) => setSettings(prev => ({ ...prev, autoSync: e.target.checked }))}
                    />
                  }
                  label="Sincronizzazione Automatica"
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Frequenza Sincronizzazione (ms)"
                  type="number"
                  value={settings.syncFrequency}
                  onChange={(e) => setSettings(prev => ({ ...prev, syncFrequency: parseInt(e.target.value) || 300000 }))}
                  helperText="Intervallo in millisecondi (300000 = 5 minuti)"
                  sx={{ mb: 2 }}
                />

                <Typography variant="body2" color="textSecondary">
                  Frequenza attuale: {Math.floor(settings.syncFrequency / 1000 / 60)} minuti
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Impostazioni Sviluppatore */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Impostazioni Sviluppatore
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.debugMode}
                    onChange={(e) => setSettings(prev => ({ ...prev, debugMode: e.target.checked }))}
                  />
                }
                label="Modalità Debug"
              />
              
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Abilita log dettagliati e informazioni di debug nella console
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Azioni */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={loading}
            >
              Ripristina Default
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Salvataggio...' : 'Salva Impostazioni'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SettingsPage;